import './styles/dispatcher.css';
import {
  ChatMessage,
  GpsCoordinates,
  RemoteCommandType,
  SessionResponse,
  WhiteboardPayload
} from '@sos/shared';
import { DispatcherSignalingService } from './services/dispatcher-signaling.service';
import { DispatcherWebRtcService } from './services/dispatcher-webrtc.service';
import { InteractiveMap } from './components/InteractiveMap';
import { SessionGeneratorModal } from './components/SessionGeneratorModal';
import { VirtualWhiteboardModal } from './components/VirtualWhiteboardModal';
import { AuditTrailDrawer } from './components/AuditTrailDrawer';
import { VideoRecorderManager } from './services/video-recorder.manager';
import { RetentionPolicyModal } from './components/RetentionPolicyModal';
import { CaseReviewModal } from './components/CaseReviewModal';
import { LoginModal, LoggedUser } from './components/LoginModal';
import { AdminConsoleModal } from './components/AdminConsoleModal';
import { AdminConsoleView } from './components/AdminConsoleView';
import { SupervisorConsoleView } from './components/SupervisorConsoleView';
import { LiveTranscriptionService } from './services/live-transcription.service';
import { LiveTranscriptionPanel } from './components/LiveTranscriptionPanel';
import { UserRole } from '@sos/shared';

class DispatcherConsoleApp {
  private appContainer: HTMLElement;
  private apiBaseUrl: string;
  private currentUser: LoggedUser | null = null;
  private currentRole: UserRole = UserRole.DISPATCHER;

  private signaling: DispatcherSignalingService | null = null;
  private webrtc: DispatcherWebRtcService | null = null;
  private map: InteractiveMap | null = null;
  private auditDrawer: AuditTrailDrawer | null = null;
  private videoRecorder: VideoRecorderManager = new VideoRecorderManager();
  private liveTranscriptionService!: LiveTranscriptionService;
  private transcriptionPanel!: LiveTranscriptionPanel;
  private transcriptionWsUrl: string;

  private currentSession: SessionResponse | null = null;
  private callTimerInterval: number | null = null;
  private callStartTimestamp: number | null = null;
  private isTorchActive: boolean = false;
  private snapshots: string[] = [];

  // Elementos del DOM
  private videoElement!: HTMLVideoElement;
  private latencyBadge!: HTMLElement;
  private timerElement!: HTMLElement;
  private folioBadge!: HTMLElement;
  private snapshotsContainer!: HTMLElement;
  private chatMessagesContainer!: HTMLElement;
  private micToggleBtn!: HTMLButtonElement;

  constructor() {
    this.appContainer = document.getElementById('app') as HTMLElement;
    // En producción con Nginx todo pasa por el mismo host (relativo ""). En desarrollo usa :4000
    this.apiBaseUrl = import.meta.env.VITE_API_URL ?? (
      window.location.port === '3001' || window.location.port === '3000'
        ? `${window.location.protocol}//${window.location.hostname}:4000`
        : ''
    );
    this.transcriptionWsUrl = import.meta.env.VITE_TRANSCRIPTION_WS_URL ?? (
      window.location.port === '3001' || window.location.port === '3000'
        ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.hostname}:5050/ws/transcribe`
        : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/transcribe`
    );
    this.liveTranscriptionService = new LiveTranscriptionService(this.transcriptionWsUrl);
  }

  public init(): void {
    const savedUserJson = sessionStorage.getItem('sos_logged_user') || sessionStorage.getItem('c5_logged_user');
    if (savedUserJson) {
      try {
        this.currentUser = JSON.parse(savedUserJson);
        this.currentRole = (this.currentUser?.role as UserRole) || UserRole.DISPATCHER;
        this.startSessionConsole();
        return;
      } catch (e) {
        sessionStorage.removeItem('sos_logged_user');
        sessionStorage.removeItem('c5_logged_user');
      }
    }

    this.promptLogin();
  }

  private promptLogin(): void {
    const loginModal = new LoginModal(this.apiBaseUrl, (user: LoggedUser) => {
      this.currentUser = user;
      this.currentRole = user.role as UserRole;
      sessionStorage.setItem('sos_logged_user', JSON.stringify(user));
      this.startSessionConsole();
    });
    loginModal.show();
  }

  private startSessionConsole(): void {
    this.renderLayout();
    this.setupGlobalActions();
    this.applyRolePermissions();

    // Comprobar si hay sesión en la URL (ej. ?sessionId=...&token=...)
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get('sessionId');
    const token = url.searchParams.get('token');

    if (sessionId && token) {
      this.attachToSession({
        sessionId,
        incidentFolio: url.searchParams.get('folio') || 'INC-ACTIVO',
        phoneNumber: '',
        citizenToken: '',
        citizenAccessUrl: '',
        spectatorToken: '',
        spectatorAccessUrl: '',
        status: 'ACTIVE' as any,
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 15 * 60000).toISOString()
      }, token);
    } else if (this.currentUser?.role === 'DISPATCHER') {
      // Mostrar modal para generar llamada de 1 clic únicamente al Paramédico / Despachador en turno
      this.openNewCallModal();
    }
  }

  private renderLayout(): void {
    this.appContainer.innerHTML = `
      <!-- Barra Superior Institucional SOS Tech Ags -->
      <header class="top-navbar">
        <div class="brand-section">
          <div class="brand-logo-diamond" title="SOS Tech Ags">
            <svg viewBox="0 0 120 120" style="width:36px; height:36px; filter: drop-shadow(0 0 8px rgba(56, 189, 248, 0.45));" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="navDiamondFrame" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#38bdf8"/>
                  <stop offset="45%" stop-color="#0284c7"/>
                  <stop offset="100%" stop-color="#0f172a"/>
                </linearGradient>
                <linearGradient id="navBevel" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#ffffff" stop-opacity="0.8"/>
                  <stop offset="100%" stop-color="#38bdf8" stop-opacity="0.2"/>
                </linearGradient>
                <linearGradient id="navSGlyph" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#ffffff"/>
                  <stop offset="35%" stop-color="#7dd3fc"/>
                  <stop offset="80%" stop-color="#0284c7"/>
                  <stop offset="100%" stop-color="#0369a1"/>
                </linearGradient>
              </defs>
              <polygon points="36,14 84,14 110,38 60,112 10,38" fill="#090d16" stroke="url(#navDiamondFrame)" stroke-width="4.5" stroke-linejoin="round"/>
              <polygon points="36,14 84,14 74,24 46,24" fill="url(#navBevel)" opacity="0.8"/>
              <polygon points="10,38 36,14 46,24 22,42" fill="#1e293b" opacity="0.9"/>
              <polygon points="110,38 84,14 74,24 98,42" fill="#0f172a" opacity="0.9"/>
              <polygon points="22,42 98,42 60,102" fill="#050a14" stroke="#0284c7" stroke-width="1.2" stroke-opacity="0.6"/>
              <path d="M 28 32 L 36 32 L 44 40" stroke="#38bdf8" stroke-width="1.6" stroke-linecap="round" fill="none" opacity="0.85"/>
              <circle cx="28" cy="32" r="2" fill="#67e8f9"/>
              <path d="M 92 32 L 84 32 L 76 40" stroke="#38bdf8" stroke-width="1.6" stroke-linecap="round" fill="none" opacity="0.85"/>
              <circle cx="92" cy="32" r="2" fill="#67e8f9"/>
              <path d="M 76 36 L 46 36 L 40 54 L 68 54 L 62 76 L 38 76 L 36 84 L 74 84 L 80 64 L 52 64 L 58 44 L 78 44 Z" fill="url(#navSGlyph)" stroke="#38bdf8" stroke-width="1.8" stroke-linejoin="miter"/>
              <path d="M 50 94 L 60 106 L 70 94" fill="none" stroke="#38bdf8" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div>
            <div class="brand-title" id="main-brand-title">
              <span>SOS TECH</span> <span style="color:#38bdf8;">[AGS]</span> <span style="color:#64748b; font-weight:300;">//</span> <span style="color:#51a2da;">SOS VideoCall™</span>
            </div>
            <div class="brand-subtitle" id="main-brand-subtitle">Tele-Triage &amp; Videollamada 911 • Centro de Comando</div>
          </div>

          <!-- Selector Unificado de Suite SOS -->
          <details class="suite-switcher" style="margin-left: 8px;">
            <summary>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>
              <span>Suite SOS</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </summary>
            <div class="suite-switcher-menu" aria-label="Productos de la suite SOS Tech Ags">
              <a href="https://sostechags.xyz/#ediciones" target="_blank" style="color:#38bdf8; font-weight:700; border-bottom:1px solid rgba(255,255,255,0.08); padding-bottom:6px; margin-bottom:4px;">
                <span>Catálogo de Soluciones</span> <span>↗</span>
              </a>
              <a href="https://sostechags.xyz/SOSVision/" target="_blank"><span>SOS Vision™ LPR</span> <span>↗</span></a>
              <a href="https://sostechags.xyz/SOSLifePulse/" target="_blank"><span>SOS LifePulse™</span> <span>↗</span></a>
              <a href="#" aria-current="page"><span>SOS VideoCall™ 911</span> <span>●</span></a>
              <a href="https://sostechags.xyz/SOSPatrolVision/" target="_blank"><span>SOS PatrolVision™</span> <span>↗</span></a>
              <a href="https://sostechags.xyz/SOSForensics/" target="_blank"><span>SOS Forensics™</span> <span>↗</span></a>
            </div>
          </details>
        </div>

        <div class="session-status-strip">
          <div class="incident-badge" id="incident-folio-badge">
            <span style="color:var(--text-muted);">FOLIO:</span>
            <strong id="folio-text">SIN SESIÓN ACTIVA</strong>
          </div>
          <div class="call-timer" id="call-timer-text">00:00</div>
        </div>

        <div class="nav-actions">
          <!-- Información del Usuario Logueado -->
          <div id="user-badge-container" style="display:flex; align-items:center; gap:8px; background:rgba(255,255,255,0.06); padding:4px 10px; border-radius:8px; border:1px solid var(--border-dim);">
            <div style="width:28px; height:28px; border-radius:50%; background:var(--primary); display:flex; align-items:center; justify-content:center; font-weight:700; font-size:0.8rem; color:#fff;">
              <span id="user-initials">911</span>
            </div>
            <div style="display:flex; flex-direction:column; line-height:1.2;">
              <span id="user-name-display" style="font-weight:700; font-size:0.82rem; color:#f8fafc;">Usuario</span>
              <div style="display:flex; align-items:center; gap:4px;">
                <span id="user-role-badge" style="font-size:0.7rem; font-weight:700; color:#38bdf8;">DISPATCHER</span>
                <span id="user-badge-num" style="font-size:0.68rem; color:var(--text-muted);">#---</span>
              </div>
            </div>
            <button id="btn-logout" title="Cerrar sesión operativa" style="background:transparent; border:none; color:#ef4444; cursor:pointer; padding:2px 4px; font-size:0.9rem; margin-left:4px;">
              🚪
            </button>
          </div>

          <button id="btn-admin-panel" class="btn-primary" style="background:#d97706; border-color:#d97706; display:none;" title="Administración del Sistema, Usuarios y Políticas">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
            ADMINISTRACIÓN
          </button>
          <button id="btn-supervisor-cases" class="btn-primary" style="background:#0284c7; border-color:#0284c7; display:none;" title="Historial forense, reportes de uso y expedientes de llamadas">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
            EXPEDIENTES OPERATIVOS
          </button>
          <button id="btn-retention-settings" class="btn-ghost" style="display:none;" title="Configurar retención y depuración de grabaciones">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
            Retención
          </button>
          <button id="btn-new-call" class="btn-primary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            NUEVA LLAMADA (1-CLIC)
          </button>
          <button id="btn-end-session" class="btn-danger" style="display:none;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect></svg>
            FINALIZAR ATENCIÓN
          </button>
        </div>
      </header>

      <!-- Grid Principal -->
      <main class="console-grid">
        <!-- Columna Izquierda: Video en Vivo y Tele-control -->
        <section class="scene-column">
          <div class="video-card">
            <video id="scene-video-player" class="video-scene-stream" autoplay playsinline></video>
            
            <div class="video-overlay-info">
              <span class="badge-live">
                <span style="width:8px; height:8px; background:#fff; border-radius:50%; display:inline-block;"></span>
                VIDEO ESCENA EN VIVO
              </span>
              <span id="latency-indicator" class="badge-latency">RTT: &lt;180ms</span>
            </div>

            <div class="video-bottom-actions">
              <div style="display:flex; gap:8px;">
                <button id="btn-toggle-mic" class="btn-ghost" title="Hablar por micrófono con el ciudadano">
                  🎤 Micrófono Activo
                </button>
              </div>

              <button id="btn-take-snapshot" class="btn-primary" style="background:#059669; border-color:#059669;">
                📸 CAPTURAR SNAPSHOT
              </button>
            </div>
          </div>

          <!-- Barra de Tele-control Remoto -->
          <div class="remote-control-panel">
            <div class="control-group">
              <span class="control-label">Linterna Móvil:</span>
              <button id="btn-remote-torch" class="toggle-switch-btn">
                🔦 Flash OFF
              </button>
            </div>

            <div class="control-group">
              <span class="control-label">Zoom Cámara:</span>
              <div class="zoom-slider-container">
                <input id="slider-zoom" type="range" class="zoom-slider" min="1" max="5" step="0.5" value="1" />
                <span id="zoom-label" style="font-family:var(--font-mono); font-size:0.8rem; width:24px;">1x</span>
              </div>
            </div>

            <div class="control-group">
              <button id="btn-remote-flip" class="btn-ghost" style="padding:6px 12px;">
                🔄 Voltear Cámara
              </button>
            </div>
          </div>

          <!-- Galería de Snapshots Clínicos -->
          <div class="snapshots-strip" id="snapshots-strip-container">
            <span style="font-size:0.75rem; color:var(--text-dim); text-transform:uppercase; font-weight:700;">Capturas:</span>
            <div id="snapshots-list" style="display:flex; gap:8px;"></div>
          </div>
        </section>

        <!-- Columna Derecha: Mapa, Apoyo Clínico y Chat/Auditoría -->
        <section class="tools-column">
          <!-- Mapa interactivo con botón de desacoplar/expandir -->
          <div class="map-card" id="main-map-card">
            <div class="map-card-header">
              <span class="map-title-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon><line x1="8" y1="2" x2="8" y2="18"></line><line x1="16" y1="6" x2="16" y2="22"></line></svg>
                OpenStreetMap GPS
              </span>
              <button id="btn-detach-map" class="btn-map-control" title="Desacoplar mapa en ventana grande">
                <svg id="icon-detach-map" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg>
                <span id="text-detach-map">Desacoplar</span>
              </button>
            </div>
            <div id="map-container"></div>
            <div id="map-telemetry" class="map-telemetry-badge">
              <span>📍 Esperando señal GPS...</span>
            </div>
          </div>

          <!-- Pestañas: Chat Silencioso, Transcripción IA y Registro de Auditoría -->
          <div class="tabs-card">
            <div class="tabs-header">
              <button id="tab-btn-chat" class="tab-btn active">Chat Silencioso</button>
              <button id="tab-btn-transcription" class="tab-btn">🎙️ Transcripción IA</button>
              <button id="tab-btn-audit" class="tab-btn">Auditoría Médica</button>
            </div>

            <!-- Contenido Chat -->
            <div id="tab-content-chat" class="tab-content" style="display:flex; flex-direction:column;">
              <div id="chat-messages-container" style="flex:1; overflow-y:auto; display:flex; flex-direction:column; gap:8px; padding-bottom:8px;"></div>
              
              <div style="display:flex; gap:8px; padding-top:8px; border-top:1px solid var(--border-dim);">
                <input id="inp-dispatcher-chat" class="form-input" style="flex:1; height:38px; font-size:0.85rem;" placeholder="Escriba indicación al ciudadano..." />
                <button id="btn-send-dispatcher-chat" class="btn-primary" style="height:38px; padding:0 14px;">Enviar</button>
              </div>
            </div>

            <!-- Contenido Transcripción y Traducción IA -->
            <div id="tab-content-transcription" class="tab-content" style="display:none; height:100%;">
              <div id="transcription-panel-slot" style="height:100%;"></div>
            </div>

            <!-- Contenido Auditoría -->
            <div id="tab-content-audit" class="tab-content" style="display:none;">
              <div id="audit-trail-slot"></div>
            </div>
          </div>
        </section>
      </main>
    `;

    this.videoElement = document.getElementById('scene-video-player') as HTMLVideoElement;
    this.latencyBadge = document.getElementById('latency-indicator') as HTMLElement;
    this.timerElement = document.getElementById('call-timer-text') as HTMLElement;
    this.folioBadge = document.getElementById('folio-text') as HTMLElement;
    this.snapshotsContainer = document.getElementById('snapshots-list') as HTMLElement;
    this.chatMessagesContainer = document.getElementById('chat-messages-container') as HTMLElement;
    this.micToggleBtn = document.getElementById('btn-toggle-mic') as HTMLButtonElement;

    // Inicializar mapa interactivo
    this.map = new InteractiveMap('map-container');
    this.map.init();
    const telemetryBox = document.getElementById('map-telemetry');
    if (telemetryBox) this.map.setTelemetryElement(telemetryBox);

    // Inicializar auditoría
    this.auditDrawer = new AuditTrailDrawer(this.apiBaseUrl);
    const auditSlot = document.getElementById('audit-trail-slot');
    if (auditSlot) this.auditDrawer.mount(auditSlot);

    // Inicializar panel de transcripción y traducción en vivo
    const videoCard = document.querySelector('.video-card') as HTMLElement;
    this.transcriptionPanel = new LiveTranscriptionPanel(videoCard, {
      onToggle: (active, targetLang) => {
        if (active) {
          const remoteStream = this.videoElement.srcObject as MediaStream;
          if (!remoteStream || remoteStream.getAudioTracks().length === 0) {
            alert('Aviso: Se requiere una videollamada activa con recepción de audio para iniciar la transcripción en vivo.');
            this.transcriptionPanel.reset();
            return;
          }
          this.liveTranscriptionService.start(
            remoteStream,
            this.currentSession?.sessionId || 'default',
            {
              onResult: (result) => {
                this.transcriptionPanel.addTranscription(result);
                this.signaling?.sendTranscriptionUpdate(result);
                this.auditDrawer?.addLiveLog('TRANSCRIPTION_CHUNK', 'CITIZEN');
              },
              onError: (err) => {
                console.warn('[LIVE_TRANSCRIPTION]', err);
                this.transcriptionPanel.setStatus(false);
              },
              onStatusChange: (status) => {
                this.transcriptionPanel.setStatus(status);
              }
            },
            targetLang
          );
        } else {
          this.liveTranscriptionService.stop();
        }
      },
      onLanguageChange: (targetLang) => {
        if (this.liveTranscriptionService.getIsRunning()) {
          const remoteStream = this.videoElement.srcObject as MediaStream;
          if (remoteStream) {
            this.liveTranscriptionService.stop();
            this.liveTranscriptionService.start(
              remoteStream,
              this.currentSession?.sessionId || 'default',
              {
                onResult: (result) => {
                  this.transcriptionPanel.addTranscription(result);
                  this.signaling?.sendTranscriptionUpdate(result);
                }
              },
              targetLang
            );
          }
        }
      }
    });
    const transcriptionSlot = document.getElementById('transcription-panel-slot');
    if (transcriptionSlot) {
      this.transcriptionPanel.mount(transcriptionSlot);
      this.transcriptionPanel.checkHealth('/transcribe/health');
    }
  }

  private setupGlobalActions(): void {
    // Botón nueva llamada
    document.getElementById('btn-new-call')?.addEventListener('click', () => {
      this.openNewCallModal();
    });

    // Botón finalizar sesión
    document.getElementById('btn-end-session')?.addEventListener('click', () => {
      this.promptEndSession();
    });

    // Linterna remota
    const torchBtn = document.getElementById('btn-remote-torch') as HTMLButtonElement;
    torchBtn?.addEventListener('click', () => {
      this.isTorchActive = !this.isTorchActive;
      this.signaling?.sendRemoteCommand({
        command: this.isTorchActive ? RemoteCommandType.TORCH_ON : RemoteCommandType.TORCH_OFF
      });

      if (this.isTorchActive) {
        torchBtn.classList.add('active');
        torchBtn.innerHTML = '🔦 Flash ON';
      } else {
        torchBtn.classList.remove('active');
        torchBtn.innerHTML = '🔦 Flash OFF';
      }
    });

    // Zoom remoto
    const zoomSlider = document.getElementById('slider-zoom') as HTMLInputElement;
    const zoomLabel = document.getElementById('zoom-label') as HTMLElement;
    zoomSlider?.addEventListener('input', () => {
      const val = parseFloat(zoomSlider.value);
      zoomLabel.textContent = `${val}x`;
      this.signaling?.sendRemoteCommand({
        command: RemoteCommandType.CAMERA_ZOOM,
        zoomValue: val
      });
    });

    // Voltear cámara remota
    document.getElementById('btn-remote-flip')?.addEventListener('click', () => {
      this.signaling?.sendRemoteCommand({
        command: RemoteCommandType.CAMERA_SWITCH
      });
    });

    // Tomar Snapshot de la escena (RF2.6)
    document.getElementById('btn-take-snapshot')?.addEventListener('click', () => {
      if (!this.webrtc) return;
      const dataUrl = this.webrtc.captureSnapshot(this.videoElement);
      if (dataUrl) {
        this.addSnapshot(dataUrl);
      } else {
        alert('Aún no se ha recibido señal de video para capturar snapshot.');
      }
    });

    // Micrófono del despachador
    this.micToggleBtn?.addEventListener('click', () => {
      const isMuted = this.micToggleBtn.classList.toggle('active');
      if (isMuted) {
        this.micToggleBtn.style.color = '#ef4444';
        this.micToggleBtn.innerHTML = '🔇 Micrófono Silenciado';
      } else {
        this.micToggleBtn.style.color = '';
        this.micToggleBtn.innerHTML = '🎤 Micrófono Activo';
      }
    });

    // Envío de chat desde el despachador
    const chatInput = document.getElementById('inp-dispatcher-chat') as HTMLInputElement;
    const sendChatBtn = document.getElementById('btn-send-dispatcher-chat') as HTMLButtonElement;

    const sendChat = () => {
      const text = chatInput.value.trim();
      if (!text || !this.signaling) return;
      this.signaling.sendChatMessage(text);
      chatInput.value = '';
    };

    sendChatBtn?.addEventListener('click', sendChat);
    chatInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') sendChat();
    });

    // Alternar pestañas
    const chatTab = document.getElementById('tab-btn-chat') as HTMLButtonElement;
    const transcriptionTab = document.getElementById('tab-btn-transcription') as HTMLButtonElement;
    const auditTab = document.getElementById('tab-btn-audit') as HTMLButtonElement;
    const chatContent = document.getElementById('tab-content-chat') as HTMLElement;
    const transcriptionContent = document.getElementById('tab-content-transcription') as HTMLElement;
    const auditContent = document.getElementById('tab-content-audit') as HTMLElement;

    chatTab?.addEventListener('click', () => {
      chatTab.classList.add('active');
      transcriptionTab?.classList.remove('active');
      auditTab.classList.remove('active');
      chatContent.style.display = 'flex';
      if (transcriptionContent) transcriptionContent.style.display = 'none';
      auditContent.style.display = 'none';
    });

    transcriptionTab?.addEventListener('click', () => {
      transcriptionTab.classList.add('active');
      chatTab.classList.remove('active');
      auditTab.classList.remove('active');
      if (transcriptionContent) transcriptionContent.style.display = 'block';
      chatContent.style.display = 'none';
      auditContent.style.display = 'none';
    });

    auditTab?.addEventListener('click', () => {
      auditTab.classList.add('active');
      chatTab.classList.remove('active');
      transcriptionTab?.classList.remove('active');
      auditContent.style.display = 'block';
      chatContent.style.display = 'none';
      if (transcriptionContent) transcriptionContent.style.display = 'none';
      if (this.currentSession) {
        this.auditDrawer?.fetchLogs(this.currentSession.sessionId);
      }
    });

    // Botón de Cerrar Sesión
    document.getElementById('btn-logout')?.addEventListener('click', () => {
      if (confirm('¿Desea cerrar la sesión del usuario operativo actual?')) {
        sessionStorage.removeItem('sos_logged_user');
        sessionStorage.removeItem('c5_logged_user');
        window.location.reload();
      }
    });

    // Modal de Administración del Sistema (ADMIN)
    const adminBtn = document.getElementById('btn-admin-panel');
    adminBtn?.addEventListener('click', () => {
      if (this.currentUser) {
        const modal = new AdminConsoleModal(this.apiBaseUrl, this.currentUser);
        modal.mount(this.appContainer);
      }
    });

    // Modal de Supervisión y Consulta de Casos (Aislado por Agencia)
    const supervisorBtn = document.getElementById('btn-supervisor-cases');
    supervisorBtn?.addEventListener('click', () => {
      const modal = new CaseReviewModal(this.apiBaseUrl, this.currentRole, this.currentUser?.agencyId, () => {});
      modal.mount(this.appContainer);
    });

    // Modal de Retención de Grabaciones
    const retentionBtn = document.getElementById('btn-retention-settings');
    retentionBtn?.addEventListener('click', () => {
      const modal = new RetentionPolicyModal(this.apiBaseUrl, () => {});
      modal.mount(this.appContainer);
    });

    // Desacoplar Mapa en Ventana Emergente Independiente
    const detachMapBtn = document.getElementById('btn-detach-map');
    detachMapBtn?.addEventListener('click', () => {
      this.map?.openInPopupWindow();
    });
  }

  private applyRolePermissions(): void {
    if (!this.currentUser) return;

    // Actualizar visualmente la información de usuario en la barra
    const initialsEl = document.getElementById('user-initials');
    const nameEl = document.getElementById('user-name-display');
    const roleBadgeEl = document.getElementById('user-role-badge');
    const badgeNumEl = document.getElementById('user-badge-num');

    if (nameEl) nameEl.textContent = this.currentUser.fullName;
    if (badgeNumEl) badgeNumEl.textContent = `#${this.currentUser.badgeNumber}`;
    if (initialsEl) {
      initialsEl.textContent = this.currentUser.fullName.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
    }

    const brandSubtitleEl = document.getElementById('main-brand-subtitle');
    if (brandSubtitleEl && this.currentUser.agencyName) {
      brandSubtitleEl.innerHTML = `${this.currentUser.agencyName} &bull; Tele-Triage &amp; Videollamada 911`;
    }

    if (roleBadgeEl) {
      roleBadgeEl.textContent = this.currentUser.role;
      if (this.currentUser.role === 'ADMIN') {
        roleBadgeEl.style.color = '#fbbf24';
      } else if (this.currentUser.role === 'SUPERVISOR') {
        roleBadgeEl.style.color = '#38bdf8';
      } else {
        roleBadgeEl.style.color = '#34d399';
      }
    }

    // Reglas de permisos por rol (RBAC):
    // 1. DISPATCHER: Operativo. Realiza llamadas y tele-triage con ciudadano. No ve expedientes globales ni admin.
    // 2. SUPERVISOR: Operativo avanzado. Supervisa expedientes, reportes y puede despachar.
    // 3. ADMIN: Tecnológico y de gobierno. No atiende llamadas ni ve consolas operativas. Gestiona usuarios, permisos, retención y estado del sistema.
    const adminBtn = document.getElementById('btn-admin-panel');
    const casesBtn = document.getElementById('btn-supervisor-cases');
    const retentionBtn = document.getElementById('btn-retention-settings');
    const newCallBtn = document.getElementById('btn-new-call');
    const consoleGrid = document.querySelector('.console-grid') as HTMLElement;

    if (this.currentUser.role === 'DISPATCHER') {
      if (adminBtn) adminBtn.style.display = 'none';
      if (casesBtn) casesBtn.style.display = 'none';
      if (retentionBtn) retentionBtn.style.display = 'none';
      if (newCallBtn) newCallBtn.style.display = 'inline-flex';
    } else if (this.currentUser.role === 'SUPERVISOR') {
      if (adminBtn) adminBtn.style.display = 'none';
      if (casesBtn) casesBtn.style.display = 'none';
      if (retentionBtn) retentionBtn.style.display = 'none';
      if (newCallBtn) newCallBtn.style.display = 'inline-flex';

      // El Supervisor tiene incrustado directamente el Centro de Expedientes y Monitoreo de Centro de Comando
      if (consoleGrid) {
        consoleGrid.style.display = 'block';
        consoleGrid.style.padding = '0';
        consoleGrid.innerHTML = '<div id="supervisor-view-mount-point" style="height:100%; width:100%;"></div>';
        const mountPoint = document.getElementById('supervisor-view-mount-point');
        if (mountPoint) {
          const supervisorView = new SupervisorConsoleView(mountPoint, this.apiBaseUrl, this.currentUser);
          supervisorView.render();
        }
      }
    } else if (this.currentUser.role === 'ADMIN') {
      if (adminBtn) adminBtn.style.display = 'none';
      if (casesBtn) casesBtn.style.display = 'none';
      if (retentionBtn) retentionBtn.style.display = 'none';
      if (newCallBtn) newCallBtn.style.display = 'none';

      // El Administrador tiene incrustado directamente todo el panel de control, espacio, retención y agencias en la pantalla
      if (consoleGrid) {
        consoleGrid.style.display = 'block';
        consoleGrid.style.padding = '0';
        consoleGrid.innerHTML = '<div id="admin-view-mount-point" style="height:100%; width:100%;"></div>';
        const mountPoint = document.getElementById('admin-view-mount-point');
        if (mountPoint) {
          const adminView = new AdminConsoleView(mountPoint, this.apiBaseUrl, this.currentUser);
          adminView.render();
        }
      }
    }
  }

  private openNewCallModal(): void {
    const modal = new SessionGeneratorModal({
      apiBaseUrl: this.apiBaseUrl,
      agencyId: this.currentUser?.agencyId || 'ag_main',
      dispatcherName: this.currentUser?.fullName || 'Despachador 911',
      onSessionCreated: (session: SessionResponse) => {
        this.attachToSession(session, 'dispatcher_auth_token');
      },
      onClose: () => {}
    });
    modal.mount(this.appContainer);
  }

  private async attachToSession(session: SessionResponse, token: string): Promise<void> {
    this.currentSession = session;
    this.folioBadge.textContent = session.incidentFolio;

    const endBtn = document.getElementById('btn-end-session');
    if (endBtn) endBtn.style.display = 'inline-flex';

    this.startCallTimer();

    // Inicializar servicios WebRTC y Señalización
    this.webrtc = new DispatcherWebRtcService();
    this.signaling = new DispatcherSignalingService(this.apiBaseUrl);

    // Inicializar micrófono local del despachador
    await this.webrtc.initializeMicrophone();

    let citizenPeerId: string | null = null;

    // Configurar PeerConnection
    this.webrtc.setupPeerConnection([], {
      onIceCandidate: (candidate) => {
        this.signaling?.sendIceCandidate(candidate, citizenPeerId || undefined);
      },
      onRemoteStream: (stream) => {
        this.videoElement.srcObject = stream;
        
        // Garantizar inicio de reproducción de video incluso bajo restricciones de autoplay
        const startPlay = async () => {
          try {
            await this.videoElement.play();
          } catch (err) {
            console.warn('[AUTOPLAY] Audio bloqueado temporalmente por navegador. Reproduciendo video silenciado...', err);
            this.videoElement.muted = true;
            try {
              await this.videoElement.play();
            } catch (e) {
              console.error('[VIDEO PLAY ERROR]', e);
            }
          }
        };

        this.videoElement.onloadedmetadata = () => {
          startPlay();
        };
        startPlay();

        this.auditDrawer?.addLiveLog('VIDEO_STREAM_ESTABLISHED', 'CITIZEN');

        // Iniciar grabación forense local del stream WebRTC
        this.videoRecorder.startRecording(stream);
        this.auditDrawer?.addLiveLog('RECORDING_STARTED', 'DISPATCHER');
      },
      onConnectionStateChange: (state) => {
        console.log('[WEBRTC PEER STATE]', state);
        if (state === 'connected') {
          this.latencyBadge.textContent = '● VIDEO CONECTADO (<120ms)';
          this.latencyBadge.style.color = '#34d399';
          this.auditDrawer?.addLiveLog('VIDEO_STREAM_CONNECTED', 'CITIZEN');
        } else if (state === 'connecting') {
          this.latencyBadge.textContent = 'Estableciendo enlace WebRTC...';
          this.latencyBadge.style.color = '#fde047';
        } else if (state === 'failed' || state === 'disconnected') {
          this.latencyBadge.textContent = 'Reconectando video...';
          this.latencyBadge.style.color = '#f87171';
        }
      }
    });

    // Conectar señalización
    await this.signaling.connect(session.sessionId, token, {
      onOffer: async ({ offer, fromPeerId }) => {
        if (!this.webrtc) return;
        citizenPeerId = fromPeerId;
        const answer = await this.webrtc.handleOfferAndCreateAnswer(offer);
        this.signaling?.sendAnswer(answer, fromPeerId);
      },
      onAnswer: async ({ answer, fromPeerId }) => {
        if (citizenPeerId && fromPeerId && fromPeerId !== citizenPeerId) return;
        await this.webrtc?.handleAnswer(answer);
      },
      onIceCandidate: async ({ candidate, fromPeerId }) => {
        if (citizenPeerId && fromPeerId && fromPeerId !== citizenPeerId) return;
        await this.webrtc?.addIceCandidate(candidate);
      },
      onPeerConnected: (peer) => {
        this.auditDrawer?.addLiveLog('PEER_CONNECTED', peer.role);
      },
      onGpsUpdate: (coords: GpsCoordinates) => {
        this.map?.updatePosition(coords);
      },
      onChatMessage: (msg: ChatMessage) => {
        this.appendChatMessage(msg);
      },
      onCallEnded: () => {
        alert('El ciudadano ha finalizado la llamada.');
        this.cleanSessionState();
      }
    });

    // Cargar historial de auditoría
    this.auditDrawer?.fetchLogs(session.sessionId);
  }

  private appendChatMessage(msg: ChatMessage): void {
    const isDispatcher = msg.senderRole === 'DISPATCHER';
    const bubble = document.createElement('div');
    bubble.style.cssText = `
      max-width: 85%;
      padding: 8px 12px;
      border-radius: 12px;
      font-size: 0.85rem;
      ${isDispatcher ? 'align-self: flex-end; background:#2563eb; color:#fff;' : 'align-self: flex-start; background:rgba(255,255,255,0.08); color:#f8fafc; border:1px solid var(--border-dim);'}
    `;
    bubble.innerHTML = `
      <div style="font-size:0.7rem; opacity:0.7; margin-bottom:2px;">${msg.senderName} (${new Date(msg.timestamp).toLocaleTimeString()})</div>
      <div>${msg.message}</div>
    `;

    this.chatMessagesContainer.appendChild(bubble);
    this.chatMessagesContainer.scrollTop = this.chatMessagesContainer.scrollHeight;
  }

  private addSnapshot(dataUrl: string): void {
    this.snapshots.push(dataUrl);

    const img = document.createElement('img');
    img.src = dataUrl;
    img.className = 'snapshot-thumb';
    img.title = 'Haga clic para abrir la pizarra y realizar anotaciones médicas';

    img.addEventListener('click', () => {
      this.openWhiteboardEditor(dataUrl);
    });

    this.snapshotsContainer.appendChild(img);
    this.auditDrawer?.addLiveLog('SNAPSHOT_CAPTURED', 'DISPATCHER');

    // Subida asíncrona a la API para persistencia forense en disco y base de datos
    if (this.currentSession?.sessionId) {
      fetch(`${this.apiBaseUrl}/api/v1/media/sessions/${this.currentSession.sessionId}/snapshots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageBase64: dataUrl,
          hasAnnotations: false
        })
      })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          this.auditDrawer?.addLiveLog('SNAPSHOT_SAVED_EVIDENCE', 'DISPATCHER');
        }
      })
      .catch(err => console.warn('[SNAPSHOT UPLOAD WARN]', err));
    }
  }

  private openWhiteboardEditor(dataUrl: string): void {
    const modal = new VirtualWhiteboardModal({
      snapshotDataUrl: dataUrl,
      onSendAnnotation: (payload: WhiteboardPayload) => {
        this.signaling?.sendWhiteboardUpdate(payload);
        this.auditDrawer?.addLiveLog('WHITEBOARD_ANNOTATION_SENT', 'DISPATCHER');

        // Guardar snapshot anotado en el expediente forense
        if (this.currentSession?.sessionId && payload.snapshotBase64) {
          fetch(`${this.apiBaseUrl}/api/v1/media/sessions/${this.currentSession.sessionId}/snapshots`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              imageBase64: payload.snapshotBase64,
              hasAnnotations: true
            })
          })
          .then(res => res.json())
          .then(data => {
            if (data.success) {
              this.auditDrawer?.addLiveLog('WHITEBOARD_SAVED_EVIDENCE', 'DISPATCHER');
            }
          })
          .catch(err => console.warn('[ANNOTATED SNAPSHOT UPLOAD WARN]', err));
        }

        alert('Anotación enviada exitosamente a la pantalla del ciudadano.');
      },
      onClose: () => {}
    });
    modal.mount(this.appContainer);
  }

  private startCallTimer(): void {
    this.callStartTimestamp = Date.now();
    this.callTimerInterval = window.setInterval(() => {
      if (!this.callStartTimestamp) return;
      const elapsed = Math.floor((Date.now() - this.callStartTimestamp) / 1000);
      const m = String(Math.floor(elapsed / 60)).padStart(2, '0');
      const s = String(elapsed % 60).padStart(2, '0');
      this.timerElement.textContent = `${m}:${s}`;
    }, 1000);
  }

  private async promptEndSession(): Promise<void> {
    if (!this.currentSession) return;

    const reason = prompt('Ingrese motivo de conclusión (ej. Ambulancia ALPHA-02 en escena):', 'Unidad de ambulancia en escena');
    if (!reason) return;

    try {
      const activeSession = this.currentSession;

      // Crear overlay visual de procesamiento forense
      const progressOverlay = document.createElement('div');
      progressOverlay.className = 'modal-backdrop';
      progressOverlay.style.zIndex = '9999';
      progressOverlay.innerHTML = `
        <div style="background:#0f172a; border:1px solid #38bdf8; border-radius:16px; padding:28px; width:440px; max-width:90%; text-align:center; box-shadow:0 20px 50px rgba(0,0,0,0.8);">
          <div style="width:56px; height:56px; border-radius:50%; background:rgba(56,189,248,0.15); border:2px solid #38bdf8; display:flex; align-items:center; justify-content:center; margin:0 auto 16px;">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" stroke-width="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>
          </div>
          <h3 style="font-size:1.2rem; color:#fff; margin:0 0 8px;">Concluyendo Atención y Procesando Video</h3>
          <p id="progress-status-text" style="font-size:0.85rem; color:#94a3b8; margin:0 0 18px;">Finalizando enlace con ciudadano y cerrando canal WebRTC...</p>
          
          <div style="background:#1e293b; border-radius:9999px; height:10px; width:100%; overflow:hidden; margin-bottom:12px;">
            <div id="progress-bar-fill" style="background:linear-gradient(90deg, #0284c7, #38bdf8); height:100%; width:30%; transition:width 0.4s ease;"></div>
          </div>
          
          <div style="font-size:0.75rem; color:#64748b; font-family:monospace;">
            Encriptando evidencia SHA-256 • Compresión FFmpeg H.264
          </div>
        </div>
      `;
      document.body.appendChild(progressOverlay);

      const statusText = progressOverlay.querySelector('#progress-status-text') as HTMLElement;
      const barFill = progressOverlay.querySelector('#progress-bar-fill') as HTMLElement;

      // 1. Notificar de inmediato por WebSocket a la sala (ciudadano y ambulancia) que la llamada ha finalizado
      this.signaling?.endCall(reason);

      // 2. Finalizar sesión en API
      await fetch(`${this.apiBaseUrl}/api/v1/sessions/${activeSession.sessionId}/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: activeSession.sessionId,
          reason,
          notes: 'Atención prehospitalaria guiada por despacho 911'
        })
      });

      if (statusText) statusText.textContent = 'Subiendo grabación HD y generando compresión forense...';
      if (barFill) barFill.style.width = '65%';

      // Detener y subir grabación al servidor para compresión automática FFmpeg
      this.auditDrawer?.addLiveLog('RECORDING_UPLOAD_START', 'DISPATCHER');
      await this.videoRecorder.stopAndUpload(
        this.apiBaseUrl,
        activeSession.sessionId,
        activeSession.incidentFolio
      );
      this.auditDrawer?.addLiveLog('RECORDING_COMPRESSED_SAVED', 'DISPATCHER');

      if (statusText) statusText.textContent = '¡Expediente guardado y video comprimido exitosamente!';
      if (barFill) barFill.style.width = '100%';

      await new Promise(r => setTimeout(r, 900));
      progressOverlay.remove();

      this.cleanSessionState();
    } catch (e) {
      alert('Error concluyendo la sesión en el servidor.');
    }
  }

  private cleanSessionState(): void {
    if (this.callTimerInterval) {
      clearInterval(this.callTimerInterval);
      this.callTimerInterval = null;
    }

    this.webrtc?.dispose();
    this.signaling?.disconnect();
    this.liveTranscriptionService.stop();
    this.transcriptionPanel.reset();

    this.currentSession = null;
    this.videoElement.srcObject = null;
    this.folioBadge.textContent = 'SIN SESIÓN ACTIVA';
    this.timerElement.textContent = '00:00';

    const endBtn = document.getElementById('btn-end-session');
    if (endBtn) endBtn.style.display = 'none';
  }
}

const app = new DispatcherConsoleApp();
app.init();
